/**
 * Settings Models Dashboard
 *
 * Per-role LLM configuration. Shows 5 role cards with the currently
 * assigned model and a picker to change it. Preferences are stored
 * per-profile in electron-store; web gets a read-only view.
 *
 * Includes per-role model filtering: users can hide specific models
 * from each role's picker via a "Manage" toggle.
 *
 * @module @humanizer/studio/components/settings/SettingsModels
 */

import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { ModelInfoTooltip } from './ModelInfoTooltip';
import { groupByRecommendation, formatCost, richModelLabel, privacyLabel } from './settings-models-utils';
import type {
  ModelRolePreferences,
  ModelRole,
  RoleFilteredModel,
} from '../../types/electron';

// ═══════════════════════════════════════════════════════════════════════════
// ROLE DEFINITIONS (mirrors packages/core/src/models/model-roles.ts)
// ═══════════════════════════════════════════════════════════════════════════

interface RoleDef {
  role: ModelRole;
  label: string;
  description: string;
  requiredCapabilities: string[];
  icon: string;
  implemented: boolean;
}

const ROLE_DEFS: RoleDef[] = [
  {
    role: 'aui',
    label: 'AUI (Conversation Agent)',
    description: 'Powers the conversational AI assistant for search, analysis, and creative work.',
    requiredCapabilities: ['chat'],
    icon: '\u2728', // sparkles
    implemented: true,
  },
  {
    role: 'embedding',
    label: 'Embedding',
    description: 'Generates vector embeddings for semantic search and clustering.',
    requiredCapabilities: ['embedding'],
    icon: '\u{1F9E9}', // puzzle
    implemented: true,
  },
  {
    role: 'vision',
    label: 'Vision',
    description: 'Processes images for OCR, captioning, and visual understanding.',
    requiredCapabilities: ['vision'],
    icon: '\u{1F441}', // eye
    implemented: true,
  },
  {
    role: 'transcription',
    label: 'Transcription',
    description: 'Converts audio to text using local Whisper models. Download models in the picker below.',
    requiredCapabilities: ['transcription'],
    icon: '\u{1F3A4}', // microphone
    implemented: true,
  },
  {
    role: 'transform',
    label: 'Transform',
    description: 'Handles text transformations: humanize, persona, style, and summarization.',
    requiredCapabilities: ['completion', 'chat'],
    icon: '\u{1F58B}', // pen
    implemented: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION DEFINITIONS (mirrors packages/core/src/models/model-roles.ts)
// ═══════════════════════════════════════════════════════════════════════════

interface OpDef {
  operation: string;
  role: ModelRole;
  label: string;
  description: string;
  demandLevel: 'high' | 'medium' | 'low';
}

const OPERATION_DEFS: OpDef[] = [
  // Transform operations
  { operation: 'transform:draft', role: 'transform', label: 'Draft Generation', description: 'Full-length draft generation (~2000+ words).', demandLevel: 'high' },
  { operation: 'transform:draft-section', role: 'transform', label: 'Section Draft', description: 'Single section draft (~500 words).', demandLevel: 'low' },
  { operation: 'transform:synthesize', role: 'transform', label: 'Synthesis', description: 'Harvest/cluster synthesis (1500-2500 words).', demandLevel: 'high' },
  { operation: 'transform:humanize', role: 'transform', label: 'Humanize', description: 'Rewrite for naturalness at any intensity.', demandLevel: 'low' },
  { operation: 'transform:persona', role: 'transform', label: 'Persona Rewrite', description: 'Rewrite in a specific persona voice.', demandLevel: 'low' },
  { operation: 'transform:style', role: 'transform', label: 'Style Transfer', description: 'Apply style transformations.', demandLevel: 'low' },
  { operation: 'transform:revision', role: 'transform', label: 'Revision Pass', description: 'Editorial revision pass.', demandLevel: 'low' },
  { operation: 'transform:summarize', role: 'transform', label: 'Summarization', description: 'Brief or detailed summaries.', demandLevel: 'low' },
  { operation: 'transform:condense', role: 'transform', label: 'Card Condensation', description: 'Condense cards for context windows.', demandLevel: 'low' },
  // AUI operations
  { operation: 'aui:chat', role: 'aui', label: 'Chat', description: 'Conversational responses.', demandLevel: 'low' },
  { operation: 'aui:plan', role: 'aui', label: 'Book Planning', description: 'Complex JSON for chapter planning.', demandLevel: 'low' },
  { operation: 'aui:outline', role: 'aui', label: 'Outline Generation', description: 'Structured JSON outline from cards.', demandLevel: 'medium' },
  { operation: 'aui:extract', role: 'aui', label: 'Extraction', description: 'Extract concepts/hooks as JSON.', demandLevel: 'medium' },
  { operation: 'aui:score', role: 'aui', label: 'Scoring', description: 'Relevance scoring as JSON.', demandLevel: 'low' },
  { operation: 'aui:hyde', role: 'aui', label: 'HYDE Generation', description: 'Hypothetical document embeddings.', demandLevel: 'low' },
  { operation: 'aui:suggest', role: 'aui', label: 'Suggestions', description: 'Short name/title suggestions.', demandLevel: 'low' },
];

/** Roles that have per-operation overrides */
const ROLES_WITH_OPERATIONS: ModelRole[] = ['aui', 'transform'];

function getOpsForRole(role: ModelRole): OpDef[] {
  return OPERATION_DEFS.filter((d) => d.role === role);
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const isElectron = typeof window !== 'undefined' && !!window.isElectron;

function privacyBadgeClass(model: RoleFilteredModel): string {
  switch (model.privacyLevel) {
    case 'local':
      return 'settings-models__badge--local';
    case 'cloud-trusted':
      return 'settings-models__badge--cloud';
    default:
      return 'settings-models__badge--third-party';
  }
}

// privacyLabel, groupByRecommendation, formatCost, richModelLabel
// are imported from ./settings-models-utils

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SettingsModels() {
  const { addToast } = useToast();
  const [prefs, setPrefs] = useState<ModelRolePreferences | null>(null);
  const [modelsCache, setModelsCache] = useState<Record<string, RoleFilteredModel[]>>({});
  const [expandedRole, setExpandedRole] = useState<ModelRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [hiddenModels, setHiddenModels] = useState<Record<ModelRole, string[]>>({
    aui: [], embedding: [], vision: [], transcription: [], transform: [],
  });
  const [managingRole, setManagingRole] = useState<ModelRole | null>(null);
  const [operationPrefs, setOperationPrefs] = useState<Record<string, string | null>>({});
  const [opsExpandedRole, setOpsExpandedRole] = useState<ModelRole | null>(null);

  // Whisper-specific state for transcription role
  const [whisperModels, setWhisperModels] = useState<Array<{ name: string; size: string; downloaded: boolean }>>([]);
  const [whisperPreferred, setWhisperPreferred] = useState<string>('ggml-base.en.bin');
  const [whisperDownloading, setWhisperDownloading] = useState<string | null>(null);
  const [quickSetupDismissed, setQuickSetupDismissed] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────

  const loadPrefs = useCallback(async () => {
    if (!isElectron || !window.electronLlm?.getModelRolePrefs) {
      setPrefs({ aui: null, embedding: null, vision: null, transcription: null, transform: null });
      setLoading(false);
      return;
    }
    const [p, hidden, opPrefs, dismissed, guideShown] = await Promise.all([
      window.electronLlm.getModelRolePrefs(),
      window.electronLlm.getHiddenModels?.() ?? { aui: [], embedding: [], vision: [], transcription: [], transform: [] },
      window.electronLlm.getOperationPrefs?.() ?? {},
      window.electronAPI?.store.get('quickSetupDismissed') as Promise<boolean | undefined>,
      window.electronAPI?.store.get('modelsGuideShown') as Promise<boolean | undefined>,
    ]);
    setPrefs(p);
    setHiddenModels(hidden);
    setOperationPrefs(opPrefs);
    if (dismissed) setQuickSetupDismissed(true);
    // Auto-expand guide on first visit
    if (!guideShown) setGuideExpanded(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  const [refreshing, setRefreshing] = useState(false);

  // Load models for a role when the picker is expanded
  const loadModelsForRole = useCallback(async (roleDef: RoleDef) => {
    if (modelsCache[roleDef.role]) return;
    if (!window.electronLlm?.getModelsForRole) return;

    const models = await window.electronLlm.getModelsForRole(roleDef.requiredCapabilities);
    setModelsCache((prev) => ({ ...prev, [roleDef.role]: models }));
  }, [modelsCache]);

  // Load whisper models when transcription role is expanded
  const loadWhisperModels = useCallback(async () => {
    if (!window.electronWhisper?.listAvailableModels) return;
    const [available, preferred] = await Promise.all([
      window.electronWhisper.listAvailableModels(),
      window.electronWhisper.getPreferredModel?.() ?? 'ggml-base.en.bin',
    ]);
    setWhisperModels(available);
    setWhisperPreferred(preferred);
  }, []);

  // Refresh: re-discover models, clear cache, reload expanded role
  const handleRefresh = useCallback(async () => {
    if (!window.electronLlm?.refreshModelsForRole || !expandedRole) return;
    setRefreshing(true);
    try {
      const roleDef = ROLE_DEFS.find(r => r.role === expandedRole);
      const models = await window.electronLlm.refreshModelsForRole(
        roleDef?.requiredCapabilities ?? []
      );
      setModelsCache((prev) => ({ ...prev, [expandedRole]: models }));
    } finally {
      setRefreshing(false);
    }
  }, [expandedRole]);

  // Listen for BYOK key changes to invalidate model cache
  useEffect(() => {
    const handler = () => setModelsCache({});
    window.addEventListener('byok-keys-changed', handler);
    return () => window.removeEventListener('byok-keys-changed', handler);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────

  const handleToggleRole = useCallback((roleDef: RoleDef) => {
    if (!roleDef.implemented) return;
    if (!isElectron) return;

    // Close manage mode and ops section if switching roles
    setManagingRole(null);
    setOpsExpandedRole(null);

    setExpandedRole((prev) => {
      const next = prev === roleDef.role ? null : roleDef.role;
      if (next) {
        if (roleDef.role === 'transcription') {
          loadWhisperModels();
        } else {
          loadModelsForRole(roleDef);
        }
      }
      return next;
    });
  }, [loadModelsForRole]);

  const handleSelectModel = useCallback(async (role: ModelRole, modelId: string | null) => {
    if (!window.electronLlm?.setModelRole) return;

    const result = await window.electronLlm.setModelRole(role, modelId);
    if (result.success) {
      setPrefs((prev) => prev ? { ...prev, [role]: modelId } : prev);
    }
  }, []);

  const handleToggleManage = useCallback((role: ModelRole) => {
    setManagingRole((prev) => prev === role ? null : role);
  }, []);

  const handleToggleModelVisibility = useCallback(async (role: ModelRole, modelId: string) => {
    const currentHidden = hiddenModels[role] ?? [];
    const isHidden = currentHidden.includes(modelId);
    const newHidden = isHidden
      ? currentHidden.filter(id => id !== modelId)
      : [...currentHidden, modelId];

    // Optimistic update
    setHiddenModels(prev => ({ ...prev, [role]: newHidden }));

    // Persist
    if (window.electronLlm?.setHiddenModels) {
      await window.electronLlm.setHiddenModels(role, newHidden);
    }
  }, [hiddenModels]);

  const handleSelectWhisperModel = useCallback(async (modelName: string) => {
    if (!window.electronWhisper?.setPreferredModel) return;
    await window.electronWhisper.setPreferredModel(modelName);
    setWhisperPreferred(modelName);
    addToast(`Whisper model set to ${modelName}`, 'success');
  }, [addToast]);

  const handleDownloadWhisperModel = useCallback(async (modelName: string) => {
    if (!window.electronWhisper?.downloadModel) return;
    setWhisperDownloading(modelName);
    const result = await window.electronWhisper.downloadModel(modelName);
    setWhisperDownloading(null);
    if (result.success) {
      addToast(`Downloaded ${modelName}`, 'success');
      loadWhisperModels(); // refresh list
    } else {
      addToast(result.error || 'Download failed', 'error');
    }
  }, [addToast, loadWhisperModels]);

  const handleToggleOps = useCallback((role: ModelRole) => {
    setOpsExpandedRole((prev) => (prev === role ? null : role));
  }, []);

  const handleSetOperationModel = useCallback(async (operation: string, modelId: string | null) => {
    if (!window.electronLlm?.setOperationPref) return;
    const result = await window.electronLlm.setOperationPref(operation, modelId);
    if (result.success) {
      setOperationPrefs((prev) => {
        const next = { ...prev };
        if (modelId === null) {
          delete next[operation];
        } else {
          next[operation] = modelId;
        }
        return next;
      });
    }
  }, []);

  const handleClearRoleOps = useCallback(async (role: ModelRole) => {
    if (!window.electronLlm?.setOperationPref) return;
    const ops = getOpsForRole(role);
    await Promise.all(ops.map((op) => window.electronLlm!.setOperationPref(op.operation, null)));
    setOperationPrefs((prev) => {
      const next = { ...prev };
      for (const op of ops) delete next[op.operation];
      return next;
    });
  }, []);

  const getActiveOverrideCount = useCallback((role: ModelRole): number => {
    const ops = getOpsForRole(role);
    return ops.filter((op) => operationPrefs[op.operation] != null).length;
  }, [operationPrefs]);

  const handleAutoConfigureOps = useCallback(async (role: ModelRole, models: RoleFilteredModel[]) => {
    if (!window.electronLlm?.setOperationPref) return;

    // 1. Filter to eligible models (available + not failed vetting)
    const eligible = models.filter(
      (m) => m.available && m.vettingStatus !== 'failed'
    );
    if (eligible.length < 2) return;

    // 2. Sort by qualityScore descending — best first
    const sorted = [...eligible].sort((a, b) => b.qualityScore - a.qualityScore);
    const bestModel = sorted[0];
    const budgetModel = sorted[sorted.length - 1];
    if (bestModel.id === budgetModel.id) return; // all same tier

    // 3. Get current role default
    const roleDefault = prefs?.[role] ?? null;

    // 4. Assign by demand level
    const ops = getOpsForRole(role);
    const updates: Record<string, string | null> = {};
    for (const op of ops) {
      let target: string | null;
      if (op.demandLevel === 'high') {
        target = bestModel.id;
      } else if (op.demandLevel === 'low') {
        target = budgetModel.id;
      } else {
        // medium → clear to role default
        target = null;
      }
      // Skip redundant overrides — if target matches role default, store null
      if (target === roleDefault) target = null;
      updates[op.operation] = target;
    }

    // 5. Batch-call setOperationPref
    await Promise.all(
      Object.entries(updates).map(([operation, modelId]) =>
        window.electronLlm!.setOperationPref(operation, modelId)
      )
    );

    // 6. Update local state
    setOperationPrefs((prev) => {
      const next = { ...prev };
      for (const [operation, modelId] of Object.entries(updates)) {
        if (modelId === null) {
          delete next[operation];
        } else {
          next[operation] = modelId;
        }
      }
      return next;
    });

    // 7. Show summary toast
    const highCount = ops.filter(o => o.demandLevel === 'high').length;
    const lowCount = ops.filter(o => o.demandLevel === 'low').length;
    const bestLabel = bestModel.name.replace(/\s*\(Your Key\)/, '').replace(/\s*\(Ollama Cloud\)/, '');
    const budgetLabel = budgetModel.name.replace(/\s*\(Your Key\)/, '').replace(/\s*\(Ollama Cloud\)/, '');
    addToast(
      `Assigned ${bestLabel} to ${highCount} demanding op${highCount !== 1 ? 's' : ''}, ${budgetLabel} to ${lowCount} simple op${lowCount !== 1 ? 's' : ''}`,
      'success',
      { duration: 5000 },
    );
  }, [prefs, addToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  const getSelectedModel = (role: ModelRole): RoleFilteredModel | null => {
    const modelId = prefs?.[role];
    if (!modelId) return null;
    const models = modelsCache[role];
    return models?.find((m) => m.id === modelId) ?? null;
  };

  const getVisibleModels = (role: ModelRole, models: RoleFilteredModel[]): RoleFilteredModel[] => {
    const hidden = new Set(hiddenModels[role] ?? []);
    if (hidden.size === 0) return models;
    const selected = prefs?.[role];
    return models.filter(m => !hidden.has(m.id) || m.id === selected);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <div>
          <h2 className="settings-section__title">Models</h2>
          <p className="settings-section__description">
            Choose which AI model powers each role. Preferences are saved per-profile.
          </p>
        </div>
      </div>

      {/* Collapsible guide for new users */}
      {isElectron && (
        <div className="settings-models__guide">
          <button
            className="settings-models__guide-toggle"
            onClick={() => {
              const next = !guideExpanded;
              setGuideExpanded(next);
              if (next) {
                window.electronAPI?.store.set('modelsGuideShown', true);
              }
            }}
            type="button"
          >
            <span className={`settings-models__ops-chevron${guideExpanded ? ' settings-models__ops-chevron--open' : ''}`}>
              &#x25B6;
            </span>
            How Model Selection Works
          </button>
          {guideExpanded && (
            <div className="settings-models__guide-content">
              <p>Each operation (drafting, chat, scoring, etc.) can use a different model.</p>
              <ul>
                <li><strong>Local models (Ollama)</strong> — Free, private, runs on your machine</li>
                <li><strong>Cloud models</strong> — Included with your humanizer.com subscription</li>
                <li><strong>BYOK models</strong> — Uses your own API key (OpenAI, Anthropic, Google)</li>
              </ul>
              <p>
                <strong>Tip:</strong> Use "Auto-Configure" inside a role's Per-Operation section to
                automatically assign your best model to demanding operations and your fastest model
                to simple ones.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Refresh button — visible when a role is expanded */}
      {expandedRole && isElectron && (
        <div className="settings-models__refresh-bar">
          <button
            className="btn btn--secondary btn--sm"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Re-discover models from Ollama and BYOK providers"
          >
            {refreshing ? 'Refreshing...' : 'Refresh Models'}
          </button>
        </div>
      )}

      <div className="settings-section__content">
        {!isElectron && (
          <div className="settings-models__web-notice">
            Model role preferences are only available in the desktop app.
            The web studio uses system defaults.
          </div>
        )}

        {/* Quick Setup banner — shown when BYOK keys exist but no model assignments */}
        {isElectron && !quickSetupDismissed && prefs && (
          // Show if all role prefs are null and all operation prefs are empty
          Object.values(prefs).every(v => v === null) &&
          Object.keys(operationPrefs).length === 0
        ) && (
          <div className="settings-models__quick-setup">
            <p>
              You have no model assignments configured. Expand a role above and use
              {' '}<strong>Auto-Configure</strong> to assign your best model to demanding operations
              and your fastest to simple ones.
            </p>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => {
                setQuickSetupDismissed(true);
                window.electronAPI?.store.set('quickSetupDismissed', true);
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="settings-models__roles">
          {ROLE_DEFS.map((roleDef) => {
            const isExpanded = expandedRole === roleDef.role;
            const selectedModel = getSelectedModel(roleDef.role);
            const allModels = modelsCache[roleDef.role] ?? [];
            const visibleModels = getVisibleModels(roleDef.role, allModels);
            const grouped = groupByRecommendation(visibleModels);
            const isManaging = managingRole === roleDef.role;
            const roleHidden = hiddenModels[roleDef.role] ?? [];

            return (
              <div
                key={roleDef.role}
                className={`settings-models__role-card${!roleDef.implemented ? ' settings-models__role-card--disabled' : ''}`}
              >
                {/* Role header — clickable to expand picker */}
                <div
                  className="settings-models__role-header"
                  onClick={() => handleToggleRole(roleDef)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleRole(roleDef); }}
                >
                  <div className="settings-models__role-icon">{roleDef.icon}</div>

                  <div className="settings-models__role-info">
                    <h3 className="settings-models__role-label">{roleDef.label}</h3>
                    <p className="settings-models__role-desc">{roleDef.description}</p>
                  </div>

                  <div className="settings-models__current">
                    {!roleDef.implemented ? (
                      <span className="settings-models__coming-soon">Coming Soon</span>
                    ) : roleDef.role === 'transcription' ? (
                      <div className="settings-models__model-info">
                        <span className="settings-models__model-name">
                          {whisperPreferred.replace('.bin', '').replace('ggml-', '')}
                        </span>
                        <span className="settings-models__badge settings-models__badge--local">Local</span>
                      </div>
                    ) : selectedModel ? (
                      <div className="settings-models__model-info">
                        <span className="settings-models__model-name">{selectedModel.name}</span>
                        <div className="settings-models__model-badges">
                          <span className={`settings-models__badge ${privacyBadgeClass(selectedModel)}`}>
                            {privacyLabel(selectedModel)}
                          </span>
                          <div className="settings-models__quality-bar">
                            <div
                              className="settings-models__quality-fill"
                              style={{ width: `${Math.round(selectedModel.qualityScore * 100)}%` }}
                            />
                          </div>
                          <ModelInfoTooltip model={selectedModel} />
                        </div>
                      </div>
                    ) : (
                      <div className="settings-models__model-info">
                        <span className="settings-models__model-name settings-models__model-name--default">
                          Auto — system default
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded model picker */}
                {isExpanded && isElectron && roleDef.implemented && (
                  <div className="settings-models__picker">
                    {roleDef.role === 'transcription' ? (
                      /* ── Whisper-specific picker ── */
                      <div className="settings-models__whisper">
                        <p className="settings-models__picker-hint">
                          Whisper runs locally on your machine. Larger models are more accurate but slower.
                        </p>
                        <div className="settings-models__whisper-list">
                          {whisperModels.map((wm) => (
                            <div key={wm.name} className="settings-models__whisper-row">
                              <div className="settings-models__whisper-info">
                                <span className="settings-models__whisper-name">
                                  {wm.name.replace('.bin', '').replace('ggml-', '')}
                                </span>
                                <span className="settings-models__whisper-size">{wm.size}</span>
                                {wm.name === whisperPreferred && (
                                  <span className="settings-models__badge settings-models__badge--local">Active</span>
                                )}
                              </div>
                              {wm.downloaded ? (
                                <button
                                  className={`btn btn--sm ${wm.name === whisperPreferred ? 'btn--primary' : 'btn--secondary'}`}
                                  onClick={() => handleSelectWhisperModel(wm.name)}
                                  disabled={wm.name === whisperPreferred}
                                >
                                  {wm.name === whisperPreferred ? 'Selected' : 'Use'}
                                </button>
                              ) : (
                                <button
                                  className="btn btn--secondary btn--sm"
                                  onClick={() => handleDownloadWhisperModel(wm.name)}
                                  disabled={whisperDownloading !== null}
                                >
                                  {whisperDownloading === wm.name ? 'Downloading...' : 'Download'}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : isManaging ? (
                      /* ── Manage mode: checkbox list ── */
                      <div className="settings-models__manage">
                        <p className="settings-models__manage-label">
                          Visible models for {roleDef.label}:
                        </p>
                        <div className="settings-models__manage-list">
                          {allModels.map((m) => {
                            const isHidden = roleHidden.includes(m.id);
                            return (
                              <label key={m.id} className="settings-models__manage-item">
                                <input
                                  type="checkbox"
                                  checked={!isHidden}
                                  onChange={() => handleToggleModelVisibility(roleDef.role, m.id)}
                                />
                                <span className="settings-models__manage-name">{m.name}</span>
                                <span className={`settings-models__badge settings-models__badge--sm ${privacyBadgeClass(m)}`}>
                                  {privacyLabel(m)}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <button
                          className="btn btn--secondary btn--sm"
                          onClick={() => setManagingRole(null)}
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      /* ── Normal mode: select + manage button ── */
                      <>
                        <div className="settings-models__picker-row">
                          <select
                            value={prefs?.[roleDef.role] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value || null;
                              handleSelectModel(roleDef.role, val);
                            }}
                          >
                            <option value="">Auto — system default</option>
                            {grouped.map((group) => (
                              <optgroup key={group.label} label={group.label}>
                                {group.models.map((m) => (
                                  <option
                                    key={m.id}
                                    value={m.id}
                                    disabled={!m.available}
                                  >
                                    {richModelLabel(m)}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {allModels.length > 0 && (
                            <button
                              className="btn btn--secondary btn--sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleManage(roleDef.role);
                              }}
                              title="Show/hide models from this role's picker"
                            >
                              Manage
                            </button>
                          )}
                        </div>
                        <p className="settings-models__picker-hint">
                          Changes take effect immediately. Restart is not required.
                          {roleHidden.length > 0 && ` (${roleHidden.length} model${roleHidden.length === 1 ? '' : 's'} hidden)`}
                        </p>

                        {/* Per-operation model overrides */}
                        {ROLES_WITH_OPERATIONS.includes(roleDef.role) && (() => {
                          const roleOps = getOpsForRole(roleDef.role);
                          const activeCount = getActiveOverrideCount(roleDef.role);
                          const isOpsOpen = opsExpandedRole === roleDef.role;

                          return (
                            <div className="settings-models__ops">
                              <button
                                className="settings-models__ops-toggle"
                                onClick={() => handleToggleOps(roleDef.role)}
                                type="button"
                              >
                                <span className={`settings-models__ops-chevron${isOpsOpen ? ' settings-models__ops-chevron--open' : ''}`}>
                                  &#x25B6;
                                </span>
                                Per-Operation Model Overrides
                                <span className="settings-models__ops-count">
                                  ({activeCount} active)
                                </span>
                              </button>

                              {isOpsOpen && (
                                <div className="settings-models__ops-panel">
                                  <p className="settings-models__demand-legend">
                                    <span className="settings-models__ops-demand settings-models__ops-demand--high">HIGH</span> = needs best model (long drafts, synthesis)
                                    {' \u00B7 '}
                                    <span className="settings-models__ops-demand settings-models__ops-demand--medium">MEDIUM</span> = moderate complexity
                                    {' \u00B7 '}
                                    LOW = fast model sufficient
                                  </p>
                                  <div className="settings-models__ops-toolbar">
                                    <button
                                      className="settings-models__ops-auto"
                                      onClick={() => handleAutoConfigureOps(roleDef.role, visibleModels)}
                                      disabled={visibleModels.filter(m => m.available && m.vettingStatus !== 'failed').length < 2}
                                      type="button"
                                      title="Assign best model to demanding ops, fastest to simple ops"
                                    >
                                      Auto
                                    </button>
                                    {activeCount > 0 && (
                                      <button
                                        className="settings-models__ops-clear"
                                        onClick={() => handleClearRoleOps(roleDef.role)}
                                        type="button"
                                      >
                                        Clear All
                                      </button>
                                    )}
                                  </div>
                                  <div className="settings-models__ops-list">
                                    {roleOps.map((op) => (
                                      <div key={op.operation} className="settings-models__ops-row">
                                        <div className="settings-models__ops-label">
                                          <div className="settings-models__ops-label-text">
                                            <span className="settings-models__ops-name">{op.label}</span>
                                            {op.demandLevel !== 'low' && (
                                              <span className={`settings-models__ops-demand settings-models__ops-demand--${op.demandLevel}`}>
                                                {op.demandLevel.toUpperCase()}
                                              </span>
                                            )}
                                          </div>
                                          <span className="settings-models__op-description">{op.description}</span>
                                        </div>
                                        <select
                                          className="settings-models__ops-select"
                                          value={operationPrefs[op.operation] ?? ''}
                                          onChange={(e) => {
                                            const val = e.target.value || null;
                                            handleSetOperationModel(op.operation, val);
                                          }}
                                          title={op.description}
                                        >
                                          <option value="">Role default</option>
                                          {grouped.map((group) => (
                                            <optgroup key={group.label} label={group.label}>
                                              {group.models.map((m) => (
                                                <option
                                                  key={m.id}
                                                  value={m.id}
                                                  disabled={!m.available}
                                                >
                                                  {richModelLabel(m)}
                                                </option>
                                              ))}
                                            </optgroup>
                                          ))}
                                        </select>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
